import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField as MuiTextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Warning as AlertIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DriverData, SnapshotFormData, CANADIAN_PROVINCES, US_STATES, EXPERIENCE_RADIUS_OPTIONS } from './types';
import { convertRadiusToEnum, parseMultiValue, stringifyMultiValue } from './utils';

// Common countries for passport selection
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin',
  'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
  'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti',
  'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Korea, North', 'Korea, South', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
  'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macao', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
  'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
  'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines',
  'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia',
  'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

interface SnapshotEditDialogProps {
  open: boolean;
  driverData: DriverData | null;
  onClose: () => void;
  onSave: (data: Partial<DriverData>) => void;
  isLoading?: boolean;
}

const defaultFormData: SnapshotFormData = {
  experienceRadius: '',
  hasCanadaExperience: null,
  canadaProvinces: [],
  canadaExperience: '',
  hasUsaExperience: null,
  usaStates: [],
  usaExperience: '',
  hasInternationalExperience: null,
  internationalExperience: '',
  hasRecentAccident: null,
  recentAccidentDescription: '',
  hasRecentTicket: null,
  recentTicketDescription: '',
  drugAlcohol3Year: null,
  canadianCitizen: null,
  canadaLiveAndWork: null,
  canEnterUsa: null,
  usaEntryStatus: null,
  hasValidPassport: null,
  passportNumber: '',
  passportExpiryDate: '',
  passportCountryOrigin: '',
  hasFastPass: false,
  fastPassNumber: '',
  hasUsDotNumber: false,
  usDotNumber: '',
};

export const SnapshotEditDialog: React.FC<SnapshotEditDialogProps> = ({
  open,
  driverData,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<SnapshotFormData>(defaultFormData);

  // Initialize form data from driverData when dialog opens
  useEffect(() => {
    if (open && driverData) {
      setFormData({
        experienceRadius: driverData.experienceRadius || '',
        hasCanadaExperience: driverData.hasCanadaExperience ?? null,
        canadaProvinces: parseMultiValue(driverData.canadaExperience),
        canadaExperience: driverData.canadaExperience || '',
        hasUsaExperience: driverData.hasUsaExperience ?? null,
        usaStates: parseMultiValue(driverData.usaExperience),
        usaExperience: driverData.usaExperience || '',
        hasInternationalExperience: driverData.hasInternationalExperience ?? null,
        internationalExperience: driverData.internationalExperience || '',
        hasRecentAccident: driverData.hasRecentAccident ?? null,
        recentAccidentDescription: driverData.recentAccidentDescription || '',
        hasRecentTicket: driverData.hasRecentTicket ?? null,
        recentTicketDescription: driverData.recentTicketDescription || '',
        drugAlcohol3Year: driverData.drugAlcohol3Year ?? null,
        canadianCitizen: driverData.canadianCitizen ?? null,
        canadaLiveAndWork: driverData.canadaLiveAndWork ?? null,
        canEnterUsa: driverData.canEnterUsa ?? null,
        usaEntryStatus: driverData.usaDenied ?? null,
        hasValidPassport: driverData.hasValidPassport ?? null,
        passportNumber: driverData.passportNumber || '',
        passportExpiryDate: driverData.passportExpiryDate || '',
        passportCountryOrigin: driverData.passportCountryOrigin || '',
        hasFastPass: driverData.hasFastPass ?? false,
        fastPassNumber: driverData.fastPassNumber || '',
        hasUsDotNumber: driverData.hasUsDotNumber ?? false,
        usDotNumber: driverData.usDotNumber || '',
      });
    } else if (!open) {
      // Reset form when dialog closes
      setFormData(defaultFormData);
    }
  }, [open]);

  const handleChange = (field: keyof SnapshotFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProvinceToggle = (province: string) => {
    setFormData(prev => ({
      ...prev,
      canadaProvinces: prev.canadaProvinces.includes(province)
        ? prev.canadaProvinces.filter(p => p !== province)
        : [...prev.canadaProvinces, province],
    }));
  };

  const handleStateToggle = (state: string) => {
    setFormData(prev => ({
      ...prev,
      usaStates: prev.usaStates.includes(state)
        ? prev.usaStates.filter(s => s !== state)
        : [...prev.usaStates, state],
    }));
  };

  const handleSave = () => {
    // Convert display value to enum value for experienceRadius
    let experienceRadiusValue = null;
    if (formData.experienceRadius) {
      const option = EXPERIENCE_RADIUS_OPTIONS.find(opt => opt.value === formData.experienceRadius);
      experienceRadiusValue = option?.enum || null;
    }

    const saveData: Partial<DriverData> = {
      experienceRadius: experienceRadiusValue,
      hasCanadaExperience: formData.hasCanadaExperience,
      canadaExperience: stringifyMultiValue(formData.canadaProvinces) || null,
      hasUsaExperience: formData.hasUsaExperience,
      usaExperience: stringifyMultiValue(formData.usaStates) || null,
      hasInternationalExperience: formData.hasInternationalExperience,
      internationalExperience: formData.internationalExperience || null,
      hasRecentAccident: formData.hasRecentAccident,
      recentAccidentDescription: formData.recentAccidentDescription || null,
      hasRecentTicket: formData.hasRecentTicket,
      recentTicketDescription: formData.recentTicketDescription || null,
      drugAlcohol3Year: formData.drugAlcohol3Year,
      canadianCitizen: formData.canadianCitizen,
      canadaLiveAndWork: formData.canadaLiveAndWork,
      canEnterUsa: formData.canEnterUsa,
      usaDenied: formData.usaEntryStatus,
      hasValidPassport: formData.hasValidPassport,
      passportNumber: formData.passportNumber || null,
      passportExpiryDate: formData.passportExpiryDate || null,
      passportCountryOrigin: formData.passportCountryOrigin || null,
      hasFastPass: formData.hasFastPass,
      fastPassNumber: formData.fastPassNumber || null,
      hasUsDotNumber: formData.hasUsDotNumber,
      usDotNumber: formData.usDotNumber || null,
    };
    onSave(saveData);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Edit Driver Profile</Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 4, overflow: 'visible', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '6px' }, '&::-webkit-scrollbar-thumb:hover': { background: '#555' } }}>
        <Stack spacing={2}>
          {/* Experience Section */}
          <Accordion defaultExpanded>
            <AccordionSummary>
              <FlagIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Experience</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3} sx={{ width: '100%' }}>
                {/* Experience Radius */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Experience Radius</InputLabel>
                  <Select
                    value={formData.experienceRadius}
                    onChange={(e) => handleChange('experienceRadius', e.target.value)}
                    label="Experience Radius"
                  >
                    {EXPERIENCE_RADIUS_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Canada Experience */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Canada Experience
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mb: 2, maxWidth: 400 }}>
                    <InputLabel>Has Canada Experience</InputLabel>
                    <Select
                      value={formData.hasCanadaExperience === null ? '' : formData.hasCanadaExperience.toString()}
                      onChange={(e) => handleChange('hasCanadaExperience', e.target.value === '' ? null : e.target.value === 'true')}
                      label="Has Canada Experience"
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.hasCanadaExperience && (
                    <FormGroup sx={{ display: 'flex', flexDirection: 'column', maxHeight: 300, overflow: 'auto' }}>
                      {CANADIAN_PROVINCES.map(province => (
                        <FormControlLabel
                          key={province}
                          control={
                            <Checkbox
                              checked={formData.canadaProvinces.includes(province)}
                              onChange={() => handleProvinceToggle(province)}
                              size="small"
                            />
                          }
                          label={province}
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>

                {/* USA Experience */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    USA Experience
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ mb: 2, maxWidth: 400 }}>
                    <InputLabel>Has USA Experience</InputLabel>
                    <Select
                      value={formData.hasUsaExperience === null ? '' : formData.hasUsaExperience.toString()}
                      onChange={(e) => handleChange('hasUsaExperience', e.target.value === '' ? null : e.target.value === 'true')}
                      label="Has USA Experience"
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.hasUsaExperience && (
                    <FormGroup sx={{ display: 'flex', flexDirection: 'column', maxHeight: 400, overflow: 'auto' }}>
                      {US_STATES.map(state => (
                        <FormControlLabel
                          key={state}
                          control={
                            <Checkbox
                              checked={formData.usaStates.includes(state)}
                              onChange={() => handleStateToggle(state)}
                              size="small"
                            />
                          }
                          label={state}
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>

                {/* International Experience */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    International Experience
                  </Typography>
                  <FormControl size="small" fullWidth sx={{ maxWidth: 400, mb: 2 }}>
                    <InputLabel>Has International Experience</InputLabel>
                    <Select
                      value={formData.hasInternationalExperience === null ? '' : formData.hasInternationalExperience.toString()}
                      onChange={(e) => handleChange('hasInternationalExperience', e.target.value === '' ? null : e.target.value === 'true')}
                      label="Has International Experience"
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.hasInternationalExperience && (
                    <MuiTextField
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Describe international experience"
                      value={formData.internationalExperience}
                      onChange={(e) => handleChange('internationalExperience', e.target.value)}
                      size="small"
                    />
                  )}
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Accident/Ticket/Citizenship Section */}
          <Accordion defaultExpanded>
            <AccordionSummary>
              <AlertIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Safety & Citizenship</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3} sx={{ width: '100%' }}>
                {/* Recent Accident */}
                <Box>
                  <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                    <InputLabel>Recent Accident (Past 3 Years)</InputLabel>
                    <Select
                      value={formData.hasRecentAccident === null ? '' : formData.hasRecentAccident.toString()}
                      onChange={(e) => handleChange('hasRecentAccident', e.target.value === '' ? null : e.target.value === 'true')}
                      label="Recent Accident (Past 3 Years)"
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.hasRecentAccident && (
                    <MuiTextField
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Describe accident"
                      value={formData.recentAccidentDescription}
                      onChange={(e) => handleChange('recentAccidentDescription', e.target.value)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                {/* Recent Ticket */}
                <Box>
                  <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                    <InputLabel>Recent Ticket (Past 3 Years)</InputLabel>
                    <Select
                      value={formData.hasRecentTicket === null ? '' : formData.hasRecentTicket.toString()}
                      onChange={(e) => handleChange('hasRecentTicket', e.target.value === '' ? null : e.target.value === 'true')}
                      label="Recent Ticket (Past 3 Years)"
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.hasRecentTicket && (
                    <MuiTextField
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Describe ticket"
                      value={formData.recentTicketDescription}
                      onChange={(e) => handleChange('recentTicketDescription', e.target.value)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                {/* Drug/Alcohol Testing */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Drug/Alcohol Testing (Past 3 Years)</InputLabel>
                  <Select
                    value={formData.drugAlcohol3Year === null ? '' : formData.drugAlcohol3Year.toString()}
                    onChange={(e) => handleChange('drugAlcohol3Year', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Drug/Alcohol Testing (Past 3 Years)"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* Citizenship */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Canadian Citizen</InputLabel>
                  <Select
                    value={formData.canadianCitizen === null ? '' : formData.canadianCitizen.toString()}
                    onChange={(e) => handleChange('canadianCitizen', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Canadian Citizen"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* Canada Live & Work */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Can Live & Work in Canada</InputLabel>
                  <Select
                    value={formData.canadaLiveAndWork === null ? '' : formData.canadaLiveAndWork.toString()}
                    onChange={(e) => handleChange('canadaLiveAndWork', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Can Live & Work in Canada"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Passport & ID Section */}
          <Accordion defaultExpanded>
            <AccordionSummary>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Passport & Identification</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3} sx={{ width: '100%' }}>
                {/* Can Enter USA */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Can Enter USA</InputLabel>
                  <Select
                    value={formData.canEnterUsa === null ? '' : formData.canEnterUsa.toString()}
                    onChange={(e) => handleChange('canEnterUsa', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Can Enter USA"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* Valid Passport */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>Valid Passport</InputLabel>
                  <Select
                    value={formData.hasValidPassport === null ? '' : formData.hasValidPassport.toString()}
                    onChange={(e) => handleChange('hasValidPassport', e.target.value === '' ? null : e.target.value === 'true')}
                    label="Valid Passport"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* Passport Number */}
                <MuiTextField
                  size="small"
                  label="Passport Number"
                  fullWidth
                  value={formData.passportNumber}
                  onChange={(e) => handleChange('passportNumber', e.target.value)}
                  placeholder="Enter passport number"
                />

                {/* Passport Expiry Date */}
                <MuiTextField
                  size="small"
                  label="Passport Expiry Date"
                  type="date"
                  fullWidth
                  value={formData.passportExpiryDate}
                  onChange={(e) => handleChange('passportExpiryDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Passport Country */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Passport Issuing Country</InputLabel>
                  <Select
                    value={formData.passportCountryOrigin}
                    onChange={(e) => handleChange('passportCountryOrigin', e.target.value)}
                    label="Passport Issuing Country"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    {COUNTRIES.map(country => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* FAST Pass */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>FAST Pass</InputLabel>
                  <Select
                    value={formData.hasFastPass === null ? '' : formData.hasFastPass.toString()}
                    onChange={(e) => handleChange('hasFastPass', e.target.value === '' ? null : e.target.value === 'true')}
                    label="FAST Pass"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* FAST Pass Number */}
                {formData.hasFastPass && (
                  <MuiTextField
                    size="small"
                    label="FAST Pass Number"
                    fullWidth
                    value={formData.fastPassNumber}
                    onChange={(e) => handleChange('fastPassNumber', e.target.value)}
                    placeholder="Enter FAST Pass number"
                  />
                )}

                {/* US DOT Number */}
                <FormControl size="small" fullWidth sx={{ maxWidth: 400 }}>
                  <InputLabel>US DOT Number</InputLabel>
                  <Select
                    value={formData.hasUsDotNumber === null ? '' : formData.hasUsDotNumber.toString()}
                    onChange={(e) => handleChange('hasUsDotNumber', e.target.value === '' ? null : e.target.value === 'true')}
                    label="US DOT Number"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>

                {/* US DOT Number Value */}
                {formData.hasUsDotNumber && (
                  <MuiTextField
                    size="small"
                    label="US DOT Number"
                    fullWidth
                    value={formData.usDotNumber}
                    onChange={(e) => handleChange('usDotNumber', e.target.value)}
                    placeholder="Enter US DOT number"
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
